import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import {SaveOutlined} from '@material-ui/icons';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import TextButton from '../../../shared/buttons/TextButton.tsx';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import {useToast} from '../../../shared/toast/ToastProvider.tsx';
import useUpdate from '../../../../hooks/useUpdate.js';
import {URL_AVALIACAO_DOCUMENTOS_ANEXO} from '../../../../constants/index.js';

const TAMANHO_MAXIMO_NOME = 150;

const FormSchema = Yup.object().shape({
    idDocumento: Yup.number().typeError('É obrigatório').required('É obrigatório'),
    nome: Yup.string().trim().required('É obrigatório').max(TAMANHO_MAXIMO_NOME, `Máximo de ${TAMANHO_MAXIMO_NOME} caracteres`)
});

const VALORES_VAZIOS = {idDocumento: '', nome: ''};

const useStyles = makeStyles(() => ({
    paper: {
        width: 500,
        maxWidth: '100%'
    },
    contentRoot: {
        overflowY: 'initial'
    }
}));

/*
 * Edita o tipo e o nome do arquivo de um anexo da inscrição.
 * `anexo` é o documento da lista de avaliação ({idInscricaoAnexo, documento, nomeArquivo});
 * `tipos` são os documentos do processo seletivo ({id, nome, descricao}).
 */
export default function DialogEditarDocumentoAnexo({open, onClose, anexo, tipos, onSalvo}) {
    const classes = useStyles();
    const update = useUpdate();
    const {createToast} = useToast();
    const {register, control, handleSubmit, reset, formState: {errors, isSubmitting}} = useForm({
        resolver: yupResolver(FormSchema),
        defaultValues: VALORES_VAZIOS
    });

    const preencher = () => reset(anexo
        ? {idDocumento: anexo.documento ? anexo.documento.id : '', nome: anexo.nomeArquivo || ''}
        : VALORES_VAZIOS);

    useEffect(() => {
        if (open) {
            preencher();
        }
    }, [open, anexo]);

    const handleClose = () => {
        onClose();
        reset(VALORES_VAZIOS);
    };

    const submit = values => update(
        URL_AVALIACAO_DOCUMENTOS_ANEXO(anexo.idInscricaoAnexo),
        {idDocumento: values.idDocumento, nome: values.nome.trim()},
        () => {
            createToast({severity: 'success', message: 'Documento atualizado com sucesso!'});
            onSalvo();
            handleClose();
        });

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionProps={{onEntering: preencher}}
            scroll='body'
            aria-labelledby='dialog-editar-documento-titulo'
            classes={{paper: classes.paper}}>
            <DialogTitle id='dialog-editar-documento-titulo'>Editar documento</DialogTitle>
            <form noValidate autoComplete='off' onSubmit={handleSubmit(submit)}>
                <DialogContent classes={{root: classes.contentRoot}}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <LabelValue label='Tipo do documento *'>
                                <FormControl variant='outlined' fullWidth error={!!errors.idDocumento}>
                                    <Controller
                                        name='idDocumento'
                                        control={control}
                                        render={({field}) => (
                                            <Select {...field} displayEmpty>
                                                <MenuItem value='' disabled><em>Selecione...</em></MenuItem>
                                                {(tipos || []).map(tipo => (
                                                    <MenuItem key={tipo.id} value={tipo.id}>{tipo.nome}</MenuItem>
                                                ))}
                                            </Select>
                                        )}/>
                                    {!!errors.idDocumento && <FormHelperText error>{errors.idDocumento.message}</FormHelperText>}
                                </FormControl>
                            </LabelValue>
                        </Grid>
                        <Grid item xs={12}>
                            <LabelValue label='Nome do arquivo *'>
                                <FormControl variant='outlined' fullWidth>
                                    <OutlinedInput
                                        {...register('nome')}
                                        error={!!errors.nome}
                                        inputProps={{maxLength: TAMANHO_MAXIMO_NOME}}/>
                                    {!!errors.nome && <FormHelperText error>{errors.nome.message}</FormHelperText>}
                                </FormControl>
                            </LabelValue>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <TextButton onClick={handleClose}>Fechar</TextButton>
                    <ContainedButton type='submit' startIcon={<SaveOutlined/>} disabled={isSubmitting}>
                        Salvar
                    </ContainedButton>
                </DialogActions>
            </form>
        </Dialog>
    );
}

DialogEditarDocumentoAnexo.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    anexo: PropTypes.object,
    tipos: PropTypes.array,
    onSalvo: PropTypes.func.isRequired
};
